// StoryOverture: the investor-room overture engine, generalized (see
// src/investor-room/Overture.js — same laws). A narrated sequence of single
// ideas that fade in over a dark stage, then hands off.
//
// Round 2 (owner feedback 2026-07-24): the "touch anywhere for sound" hint got
// lost at the bottom. Now: autoplay is ATTEMPTED silently on mount; when the
// browser blocks it, a centered START GATE fronts the show — one obvious
// "Start the presentation" button (the click is the audio gesture, so sound
// always works) plus a "watch without sound" path that advances slides on a
// timer. A sound on/off toggle sits bottom-left for the whole run; unmuting
// from silent mode seeks the narration to the current slide's cue.
//
// Slide cues are WEIGHTS, not timestamps: on loadedmetadata they scale to the
// real audio duration, so re-recording narration never desyncs the slides.
// Slide kinds: "stat" {big, line, sub} · "line" {big, sub} · "pills" {items,
// hot} (hot = index rendered in the accent color).
import React, { useCallback, useEffect, useRef, useState } from "react";

const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";
const SANS = "'Hanken Grotesk',system-ui,sans-serif";

export default function StoryOverture({
  slides, audioSrc, tagline, onDone, gateTitle = "A 60 second story.",
  accent = "#74a0f5", paper = "#f1f2f4", dim = "#b6b9c0",
  skipLabel = "Skip to the page", fallbackSecs = 5.2, totalFallbackSecs = 62,
}) {
  const audioRef = useRef(null);
  const [i, setI] = useState(0);
  const [gate, setGate] = useState(true);    // start overlay until audio runs (or silent chosen)
  const [silent, setSilent] = useState(false); // timer-driven, no narration
  const [muted, setMuted] = useState(false);
  const cuesRef = useRef(null);
  const doneRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    try { audioRef.current && audioRef.current.pause(); } catch (e) { /* gone */ }
    onDone();
  }, [onDone]);

  // Attempt autoplay once on mount. Success -> straight into the show with
  // sound. Blocked -> the start gate stays up (no lost bottom hint).
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => setGate(false)).catch(() => { /* gate stays */ });
    } else {
      // legacy/jsdom: play() returns nothing — assume it played
      setGate(false);
    }
  }, []);

  const start = () => { // the button click is the audio gesture
    const a = audioRef.current;
    const p = a && a.play();
    if (p && typeof p.then === "function") {
      p.then(() => { setGate(false); setSilent(false); })
        .catch(() => { setGate(false); setSilent(true); setMuted(true); });
    } else {
      setGate(false);
    }
  };
  const startSilent = () => { setGate(false); setSilent(true); setMuted(true); };

  const toggleSound = () => {
    const a = audioRef.current;
    if (silent) {
      // join the narration at the current slide's cue
      const cues = cuesRef.current;
      if (a && cues) { try { a.currentTime = i > 0 ? cues[i - 1] : 0; } catch (e) { /* not seekable yet */ } }
      const p = a && a.play();
      if (p && typeof p.then === "function") {
        p.then(() => { setSilent(false); setMuted(false); }).catch(() => {});
      }
      return;
    }
    if (a) { a.muted = !muted; }
    setMuted(!muted);
  };

  const onMeta = () => {
    const d = (audioRef.current && audioRef.current.duration) || totalFallbackSecs;
    const total = slides.reduce((n, s) => n + s.w, 0);
    let acc = 0;
    cuesRef.current = slides.map((s) => { acc += s.w; return (acc / total) * d; });
  };

  const onTime = () => {
    const cues = cuesRef.current;
    const t = (audioRef.current && audioRef.current.currentTime) || 0;
    if (!cues) return;
    let idx = cues.findIndex((c) => t < c);
    if (idx === -1) idx = slides.length - 1;
    if (idx !== i) setI(idx);
  };

  // Silent mode: slides advance on a timer, and the show still ends.
  useEffect(() => {
    if (gate || !silent) return undefined;
    const t = setInterval(() => setI((n) => {
      if (n + 1 < slides.length) return n + 1;
      clearInterval(t);
      setTimeout(finish, fallbackSecs * 1000);
      return n;
    }), fallbackSecs * 1000);
    return () => clearInterval(t);
  }, [gate, silent, slides.length, fallbackSecs, finish]);

  const s = slides[i];
  const fade = { animation: "soIn .9s ease-out both" };
  const pillBtn = {
    fontFamily: MONO, fontSize: 11.5, letterSpacing: ".1em", textTransform: "uppercase",
    cursor: "pointer", color: "#9aa1ad", background: "transparent",
    border: "1px solid rgba(241,242,244,.2)", borderRadius: 999, padding: "8px 14px",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0c0d10", color: paper,
      display: "grid", placeItems: "center", padding: "6vw", overflow: "hidden",
      fontFamily: SANS }}
         data-testid="story-overture">
      <style>{`
        @keyframes soIn { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform:none; } }
        @keyframes soGlow { 0%,100% { opacity:.35 } 50% { opacity:.6 } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important } }
      `}</style>

      {/* ambient */}
      <div style={{ position: "absolute", width: "70vw", height: "70vw", borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}17 0%, transparent 62%)`,
        animation: "soGlow 7s ease-in-out infinite", pointerEvents: "none" }} />

      {gate ? (
        /* ————— the start gate: one obvious way in ————— */
        <div data-testid="story-gate" style={{ ...fade, textAlign: "center", zIndex: 2, maxWidth: "min(700px,90vw)" }}>
          <div style={{ fontFamily: MONO, fontSize: 12, letterSpacing: ".24em", textTransform: "uppercase",
            color: accent, marginBottom: 22 }}>{tagline}</div>
          <div style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(32px,5.6vw,58px)",
            lineHeight: 1.1, marginBottom: 34 }}>{gateTitle}</div>
          <button onClick={start} data-testid="story-start"
                  style={{ fontFamily: SANS, fontSize: 17, fontWeight: 600, color: "#0c0d10",
                    background: accent, border: "none", cursor: "pointer",
                    padding: "16px 34px", borderRadius: 999 }}>
            ▶&nbsp;&nbsp;Start the presentation
          </button>
          <div style={{ marginTop: 18 }}>
            <button onClick={startSilent} data-testid="story-start-silent"
                    style={{ fontFamily: MONO, fontSize: 11.5, letterSpacing: ".1em",
                      textTransform: "uppercase", cursor: "pointer", color: "#7c818b",
                      background: "transparent", border: "none", textDecoration: "underline" }}>
              watch without sound
            </button>
          </div>
        </div>
      ) : (
        /* ————— the slide ————— */
        <div key={i} style={{ ...fade, textAlign: "center", maxWidth: "min(1000px,90vw)", zIndex: 2 }}>
          {s.kind === "stat" && (
            <>
              <div style={{ fontFamily: MONO, fontSize: "clamp(52px,13vw,150px)", color: accent,
                lineHeight: 1, letterSpacing: "-.02em" }}>{s.big}</div>
              {s.line && (
                <div style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(22px,3.6vw,40px)",
                  marginTop: 14, letterSpacing: "-.01em" }}>{s.line}</div>
              )}
            </>
          )}
          {s.kind === "line" && (
            <div style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,5.6vw,64px)",
              lineHeight: 1.1, letterSpacing: "-.01em", maxWidth: "20ch", marginInline: "auto" }}>{s.big}</div>
          )}
          {s.kind === "shot" && (
            <div style={{ maxWidth: "min(920px,92vw)", marginInline: "auto" }}>
              <img src={s.src} alt={s.alt || ""} style={{ width: "100%", borderRadius: 14,
                border: "1px solid rgba(241,242,244,0.18)", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.85)" }} />
              {s.line && (
                <div style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(20px,3vw,32px)", marginTop: 18 }}>{s.line}</div>
              )}
            </div>
          )}
          {s.kind === "pills" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 14px", justifyContent: "center",
              maxWidth: "min(760px,86vw)", marginInline: "auto" }}>
              {s.items.map((x, n) => (
                <span key={x} style={{ fontFamily: MONO, fontSize: "clamp(13px,2.2vw,22px)",
                  letterSpacing: ".14em", textTransform: "uppercase",
                  color: n === s.hot ? accent : paper,
                  border: `1px solid ${n === s.hot ? accent : "rgba(241,242,244,.3)"}`,
                  borderRadius: 999, padding: "10px 20px",
                  animation: `soIn .5s ease-out ${n * 0.28}s both` }}>{x}</span>
              ))}
            </div>
          )}
          {s.sub && (
            <div style={{ fontFamily: SANS, fontSize: "clamp(15px,2.2vw,22px)",
              color: dim, marginTop: 22, maxWidth: "44ch", marginInline: "auto", lineHeight: 1.5,
              animation: "soIn .9s ease-out .35s both" }}>{s.sub}</div>
          )}
        </div>
      )}

      {/* who this is, quiet, top left */}
      {!gate && (
        <div style={{ position: "absolute", top: 26, left: 30, fontFamily: MONO, fontSize: 10.5,
          letterSpacing: ".18em", textTransform: "uppercase", color: "#6d737f" }}>
          {tagline}
        </div>
      )}

      {/* progress */}
      {!gate && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
          background: "rgba(241,242,244,.08)" }}>
          <div style={{ height: "100%", background: accent, transition: "width .6s linear",
            width: `${((i + 1) / slides.length) * 100}%` }} />
        </div>
      )}

      {/* sound toggle — always visible during the show */}
      {!gate && (
        <button onClick={toggleSound} data-testid="story-sound"
                title={silent || muted ? "Turn sound on" : "Turn sound off"}
                style={{ ...pillBtn, position: "absolute", bottom: 26, left: 30 }}>
          {silent || muted ? "🔇 sound off" : "🔊 sound on"}
        </button>
      )}

      {/* always escapable */}
      <button onClick={finish} data-testid="story-skip"
              style={{ ...pillBtn, position: "absolute", bottom: 26, right: 30, border: "none" }}>
        {skipLabel} →
      </button>

      <audio ref={audioRef} src={audioSrc} preload="auto"
             onLoadedMetadata={onMeta} onTimeUpdate={onTime} onEnded={finish} />
    </div>
  );
}
